<script setup>
// Configurações: o que não responde "quem é esse agente" mas precisa existir em algum lugar.
// A Identidade ficou com prompt e modelo — as duas coisas que definem o comportamento. Nome,
// debounce, ligar/desligar e apagar vieram para cá, junto com o id do bot, que ninguém decora
// e todo mundo precisa copiar quando vai depurar integração.
//
// O time de fallback saiu em 22/09/2026: ele só servia ao erro do `assign_to_team`, que saiu
// do catálogo, e o runtime nativo nunca leu `fallback_team_id`. O valor gravado fica no config;
// o save daqui só deixou de mandá-lo — mandar nulo o apagaria.
import { ref, computed, inject, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { mensagemDoErro } from '../erros';
import powerAgentsApi from 'dashboard/api/powerAgents';
import teamsApi from 'dashboard/api/teams';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SectionShell from './SectionShell.vue';
import { useRascunhoDaSecao } from '../useRascunhoDaSecao';
import { useAdmin } from 'dashboard/composables/useAdmin';
import AgentPhoto from '../AgentPhoto.vue';

const props = defineProps({ agent: { type: Object, required: true } });

// ISO com fuso -> o formato que o <input type="datetime-local"> entende (local, sem fuso).
function paraCampoLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// E a volta. `toISOString` já devolve UTC com o `Z`, que é o que a API valida.
function paraISO(local) {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const { t } = useI18n();
const ctx = inject('powerAgentContext');
const { isSuperAdmin } = useAdmin();

// Nasce LIGADO, ao contrario de `read_attachments` — e o runtime trata ausente como ligado,
// entao a frota ja publicada nao precisa republicar para ter rastro. Ler anexo gasta no turno
// em que chega; aqui o custo e uma coluna de texto, e o valor e estar ligado QUANDO o defeito
// aparece. So super admin ve e escreve: decide o que fica GRAVADO, nao como o agente atende.
const rastroCompleto = ref(props.agent.config?.trace_completo ?? true);

// Gravada no bot na hora, fora do Save: mantém o agente em memória coerente com o que a tela
// mostra sem forçar um refetch da página inteira.
function onFotoTrocada(url) {
  ctx.onSectionSaved({ ...props.agent, avatar_url: url });
}

const botName = ref(props.agent.bot_name || '');

// A janela de manutenção. `enabled` continua existindo no modelo e é o que a lista de
// agentes usa; o que saiu foi o INTERRUPTOR desta tela — desligar sem prazo e sem texto era
// o que deixava cliente no vácuo e agente parado por dias.
//
// O <input type="datetime-local"> fala o horário LOCAL de quem edita e não carrega fuso; a
// API quer ISO com fuso. As duas conversões ficam aqui, e não no servidor, porque só o
// navegador sabe em que fuso a pessoa está.
const manutencaoDe = ref(paraCampoLocal(props.agent.config?.maintenance_from));
const manutencaoAte = ref(
  paraCampoLocal(props.agent.config?.maintenance_until)
);
const manutencaoTexto = ref(props.agent.config?.maintenance_message || '');
const manutencaoTime = ref(props.agent.config?.maintenance_team_id || '');
// PERF-01 (F2-T10): o que vai ao cliente quando o agente desiste do turno e não houve passagem
// para humano. Em branco, o runtime manda o texto padrão no idioma da conversa. O limite é o da
// API (`CONTINGENCIA_MAX` no power_agents_controller.rb).
const contingenciaTexto = ref(props.agent.config?.contingency_text || '');
const CONTINGENCIA_MAX = 1000;
const debounceSeconds = ref(props.agent.config?.debounce_seconds ?? 0);
const teams = ref([]);
const saving = ref(false);
const copiado = ref(false);

const botId = computed(() => props.agent.agent_bot_id || props.agent.id);

// A janela está aberta AGORA? É o que justifica a etiqueta na tela: sem ela, duas datas
// passadas e uma janela em curso têm a mesma aparência, e a diferença é o agente estar ou
// não atendendo neste minuto.
const janelaAtiva = computed(() => {
  if (!manutencaoDe.value || !manutencaoAte.value) return false;
  const agora = Date.now();
  return (
    new Date(manutencaoDe.value).getTime() < agora &&
    agora < new Date(manutencaoAte.value).getTime()
  );
});

function limparJanela() {
  manutencaoDe.value = '';
  manutencaoAte.value = '';
}

async function copiarId() {
  try {
    await navigator.clipboard.writeText(String(botId.value));
    copiado.value = true;
    setTimeout(() => {
      copiado.value = false;
    }, 1800);
  } catch (e) {
    // Ambiente sem permissão de clipboard (http, iframe): melhor dizer que não deu do que
    // deixar o botão piscar "copiado" sem ter copiado nada.
    useAlert(t('POWER_AGENTS.SETTINGS.COPY_FAILED'));
  }
}

onMounted(async () => {
  try {
    const { data } = await teamsApi.get();
    teams.value = data || [];
  } catch (e) {
    teams.value = [];
  }
});

// O que a pessoa edita aqui; a página avisa antes de perder (ver useRascunhoDaSecao).
const { suja, limpar, definirSalvar } = useRascunhoDaSecao(() => ({
  rastroCompleto: rastroCompleto.value,
  botName: botName.value,
  manutencaoDe: manutencaoDe.value,
  manutencaoAte: manutencaoAte.value,
  manutencaoTexto: manutencaoTexto.value,
  manutencaoTime: manutencaoTime.value,
  contingenciaTexto: contingenciaTexto.value,
  debounceSeconds: debounceSeconds.value,
}));

async function save() {
  saving.value = true;
  try {
    const updated = await powerAgentsApi.update(
      ctx.accountId(),
      ctx.agentId(),
      {
        bot_name: botName.value.trim(),
        config: {
          debounce_seconds: Number(debounceSeconds.value) || 0,
          // Nulo REMOVE a chave (o config é merge desde o T10.4). É assim que se cancela
          // uma janela: limpar os campos e salvar, sem precisar de um botão que apague por
          // fora.
          maintenance_from: paraISO(manutencaoDe.value),
          maintenance_until: paraISO(manutencaoAte.value),
          maintenance_message: manutencaoTexto.value.trim() || null,
          maintenance_team_id: manutencaoTime.value || null,
          contingency_text: contingenciaTexto.value.trim() || null,
          // Fora do alcance do cliente no servidor tambem (`CONFIG_KEYS_SUPER_ADMIN_ONLY`);
          // mandar a chave sem ser super admin faria a API recusar o save inteiro.
          ...(isSuperAdmin.value
            ? { trace_completo: rastroCompleto.value }
            : {}),
        },
      }
    );
    ctx.onSectionSaved(updated);
    useAlert(t('POWER_AGENTS.SAVED'));
    limpar();
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.SAVE_ERROR')));
  } finally {
    saving.value = false;
  }
}
definirSalvar(save);
</script>

<template>
  <SectionShell
    :title="t('POWER_AGENTS.SECTIONS.SETTINGS')"
    :description="t('POWER_AGENTS.SETTINGS.DESCRIPTION')"
    :saving="saving"
    :suja="suja"
    @save="save"
  >
    <!-- A foto vive aqui, ao lado do nome: as duas respondem "como este agente aparece",
         e estavam em abas diferentes. Ela é gravada no bot na hora, fora do Save da seção. -->
    <AgentPhoto
      data-busca="photo"
      :agent="agent"
      :account-id="ctx.accountId()"
      @updated="onFotoTrocada"
    />

    <Input
      v-model="botName"
      data-busca="bot-name"
      :label="t('POWER_AGENTS.IDENTITY.BOT_NAME')"
    />

    <!-- Id do bot: só de leitura, com botão de copiar. É o que se cola num curl ou num
         suporte, e digitar à mão erra. -->
    <div data-busca="bot-id">
      <label class="block mb-1 text-sm font-medium text-n-slate-12">
        {{ t('POWER_AGENTS.SETTINGS.BOT_ID') }}
      </label>
      <div class="flex items-center gap-2">
        <code
          class="flex-1 px-3 py-2 font-mono text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-11"
        >
          {{ botId }}
        </code>
        <Button
          :icon="copiado ? 'i-lucide-check' : 'i-lucide-copy'"
          :label="
            copiado
              ? t('POWER_AGENTS.SETTINGS.COPIED')
              : t('POWER_AGENTS.SETTINGS.COPY')
          "
          slate
          sm
          @click="copiarId"
        />
      </div>
    </div>

    <Input
      v-model="debounceSeconds"
      data-busca="debounce"
      type="number"
      min="0"
      :label="t('POWER_AGENTS.MEDIA.DEBOUNCE_SECONDS')"
    />

    <!-- A janela de manutenção substituiu o interruptor de ligado/desligado, e a troca
         conserta dois defeitos dele: quem escrevia com o agente desligado não recebia NADA,
         e o desligamento não tinha prazo — dependia de alguém lembrar de religar.

         Âmbar e não vermelho: tirar o agente do ar por uma hora é reversível e tem hora
         para acabar; apagar não. Dois avisos da mesma cor ensinariam a ler os dois com o
         mesmo peso. -->
    <div
      data-busca="maintenance"
      class="p-4 mt-2 border rounded-xl border-n-amber-6 bg-n-amber-2"
    >
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="min-w-0">
          <h3 class="text-sm font-semibold text-n-amber-11">
            {{ t('POWER_AGENTS.MAINTENANCE.TITLE') }}
          </h3>
          <p class="mt-1 text-xs text-n-slate-11">
            {{ t('POWER_AGENTS.MAINTENANCE.HINT') }}
          </p>
        </div>
        <span
          v-if="janelaAtiva"
          class="px-2 py-1 text-xs font-medium rounded-full bg-n-amber-9 text-white"
        >
          {{ t('POWER_AGENTS.MAINTENANCE.ACTIVE_NOW') }}
        </span>
      </div>

      <div class="grid gap-3 mt-3 sm:grid-cols-2">
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('POWER_AGENTS.MAINTENANCE.FROM') }}
          <input
            v-model="manutencaoDe"
            type="datetime-local"
            class="px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('POWER_AGENTS.MAINTENANCE.UNTIL') }}
          <input
            v-model="manutencaoAte"
            type="datetime-local"
            class="px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
          />
        </label>
      </div>

      <!-- O texto tem padrão no runtime, mas o campo fica em branco de propósito: um texto
           pré-escrito na tela seria publicado sem ninguém ler. -->
      <label class="flex flex-col gap-1 mt-3 text-xs text-n-slate-11">
        {{ t('POWER_AGENTS.MAINTENANCE.MESSAGE') }}
        <textarea
          v-model="manutencaoTexto"
          rows="2"
          :placeholder="t('POWER_AGENTS.MAINTENANCE.MESSAGE_PLACEHOLDER')"
          class="px-3 py-2 text-sm border rounded-lg outline-none resize-y border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
        />
      </label>

      <label class="flex flex-col gap-1 mt-3 text-xs text-n-slate-11">
        {{ t('POWER_AGENTS.MAINTENANCE.TEAM') }}
        <select
          v-model="manutencaoTime"
          class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
        >
          <option value="">
            {{ t('POWER_AGENTS.MAINTENANCE.TEAM_NONE') }}
          </option>
          <option v-for="time in teams" :key="time.id" :value="String(time.id)">
            {{ time.name }}
          </option>
        </select>
      </label>

      <Button
        v-if="manutencaoDe || manutencaoAte"
        :label="t('POWER_AGENTS.MAINTENANCE.CLEAR')"
        icon="i-lucide-x"
        slate
        link
        xs
        class="mt-2"
        @click="limparJanela"
      />
    </div>

    <!-- A outra resposta que sai no lugar do agente. Em branco de propósito, como a da janela:
         a dica mostra o padrão, e um texto pré-escrito seria publicado sem ninguém ler. -->
    <div
      data-busca="contingency"
      class="p-4 mt-2 border rounded-xl border-n-weak"
    >
      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.SETTINGS.CONTINGENCY_LABEL') }}
        </span>
        <textarea
          v-model="contingenciaTexto"
          rows="2"
          :maxlength="CONTINGENCIA_MAX"
          :placeholder="t('POWER_AGENTS.SETTINGS.CONTINGENCY_PLACEHOLDER')"
          class="px-3 py-2 mb-0 text-sm border rounded-lg outline-none resize-y border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
        />
        <span class="text-n-slate-10">
          {{ t('POWER_AGENTS.SETTINGS.CONTINGENCY_HINT') }}
        </span>
      </label>
    </div>

    <div
      v-if="isSuperAdmin"
      data-busca="trace-completo"
      class="flex items-start gap-3 p-4 mt-2 border rounded-xl border-n-weak"
    >
      <Switch v-model="rastroCompleto" class="mt-0.5" />
      <div class="min-w-0">
        <span class="block text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.SETTINGS.TRACE_FULL') }}
        </span>
        <span class="block mt-0.5 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.SETTINGS.TRACE_FULL_HINT') }}
        </span>
      </div>
    </div>

    <div
      data-busca="delete-agent"
      class="p-4 mt-2 border rounded-xl border-n-ruby-6 bg-n-ruby-2"
    >
      <div class="flex flex-wrap items-center justify-between gap-4">
        <div class="min-w-0">
          <h3 class="text-sm font-semibold text-n-ruby-11">
            {{ t('POWER_AGENTS.IDENTITY.DANGER_TITLE') }}
          </h3>
          <p class="mt-1 text-xs text-n-slate-11">
            {{ t('POWER_AGENTS.IDENTITY.DANGER_HINT') }}
          </p>
        </div>
        <Button
          :label="t('POWER_AGENTS.IDENTITY.DANGER_DELETE')"
          icon="i-lucide-trash-2"
          ruby
          sm
          @click="ctx.openDelete()"
        />
      </div>
    </div>
  </SectionShell>
</template>
